import Group from 'components/utils/Group'
import type { ComponentProps } from 'react'
import styled from 'styled-components'
import PriceRowLuxPlusNonMemberPrice from './LuxPlus/PriceRowLuxPlusNonMemberPrice'
import PriceRowLuxPlusPrice from './LuxPlus/PriceRowLuxPlusPrice'
import type PriceRowPrice from './PriceRowPrice'

const Stack = styled(Group)`
  &.h-align-start {
    text-align: start;
  }
  &.h-align-center {
    text-align: center;
  }
  &.h-align-end {
    text-align: end;
  }
`

type InheritedPriceRowPriceProps = Pick<ComponentProps<typeof PriceRowPrice>, 'saleUnit' | 'size' | 'signDisplay'>

interface Props extends InheritedPriceRowPriceProps {
  memberPrice: number
  nonMemberPrice: ComponentProps<typeof PriceRowPrice>['price']
  /**
   * @default start
   */
  horizontalAlign?: 'start' | 'center' | 'end'
  desktopHorizontalAlign?: 'start' | 'center' | 'end'
  testid?: string
  optimizelyExperiment?: string
  orientation?: 'vertical' | 'horizontal'
  /** Optional compact comparison layout; the default stack is unchanged. */
  comparisonPresentation?: 'inline' | 'caption'
}

function PriceStackPriceWithLoyaltyLeadWithLuxPlusPrice(props: Props) {
  const {
    memberPrice,
    nonMemberPrice,
    saleUnit,
    size,
    horizontalAlign = 'start',
    desktopHorizontalAlign,
    signDisplay,
    orientation,
    comparisonPresentation,
    ...rest
  } = props
  const hasComparison = memberPrice > 0 && nonMemberPrice > memberPrice
  const inlineComparison = comparisonPresentation === 'inline' && hasComparison
  const showCaption = !comparisonPresentation || (comparisonPresentation === 'caption' && hasComparison)
  // CRO-7023: anchor the member price at the outer edge of right-aligned treatments.
  const leadWithComparison = !!comparisonPresentation && horizontalAlign === 'end'

  return <Stack
    {...rest}
    className={`h-align-${horizontalAlign}`}
    direction="vertical"
    horizontalAlign={horizontalAlign}
    gap={2}
    desktopHorizontalAlign={desktopHorizontalAlign}
  >
    <PriceRowLuxPlusPrice
      memberPrice={memberPrice}
      saleUnit={saleUnit}
      logoPlacement={inlineComparison ? undefined : (leadWithComparison ? 'leading' : 'trailing')}
      hideLogoPrefix={!!comparisonPresentation}
      nonMemberPrice={inlineComparison ? nonMemberPrice : undefined}
      nonMemberPricePlacement={inlineComparison && !leadWithComparison ? 'trailing' : undefined}
      size={size}
      signDisplay={signDisplay}
    />
    {showCaption && <PriceRowLuxPlusNonMemberPrice
      priceWeight={comparisonPresentation ? 'normal' : undefined}
      nonMemberPrice={nonMemberPrice}
      signDisplay={signDisplay}
    />}
  </Stack>
}

export default PriceStackPriceWithLoyaltyLeadWithLuxPlusPrice
